import { useEffect, useState } from 'react';
import type { Session } from '../App';
import { Container, PageHeader } from '../App';

interface Props { session: Session; }

interface BoardSummary {
  board_id: string;
  display_name: string;
  total_entries: number;
}

interface RankEntry {
  rank: number;
  player_id: string;
  score: number;
}

export default function Leaderboards({ session }: Props) {
  const [boards, setBoards] = useState<BoardSummary[]>([]);
  const [selectedBoard, setSelectedBoard] = useState('');
  const [entries, setEntries] = useState<RankEntry[]>([]);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Submit form
  const [submitPlayer, setSubmitPlayer] = useState('');
  const [submitScore, setSubmitScore] = useState('');

  const limit = 20;
  const base = `/api/v1/games/${session.gameId}/modules/leaderboard`;

  const reloadBoards = () => {
    fetch(`${base}/boards`)
      .then(r => r.json())
      .then((d: { boards: BoardSummary[] }) => {
        setBoards(d.boards);
        if (d.boards.length > 0 && !selectedBoard) setSelectedBoard(d.boards[0].board_id);
      })
      .catch(e => setError(e.message));
  };

  const reloadEntries = () => {
    if (!selectedBoard) return;
    fetch(`${base}/boards/${selectedBoard}?offset=${offset}&limit=${limit}`)
      .then(r => r.json())
      .then((d: { entries: RankEntry[] }) => setEntries(d.entries))
      .catch(e => setError(e.message));
  };

  useEffect(() => { reloadBoards(); }, [session.gameId]);
  useEffect(() => { reloadEntries(); }, [selectedBoard, offset, session.gameId]);

  const submitScoreAction = async () => {
    if (!selectedBoard || !submitPlayer.trim() || !submitScore) return;
    setBusy(true); setError(null);
    try {
      const res = await fetch(`${base}/boards/${selectedBoard}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ player_id: submitPlayer.trim(), score: Number(submitScore) }),
      });
      if (!res.ok) throw new Error(await res.text());
      setSubmitPlayer(''); setSubmitScore('');
      reloadEntries();
      reloadBoards();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const resetBoard = async () => {
    if (!selectedBoard) return;
    setBusy(true); setError(null);
    try {
      const res = await fetch(`${base}/boards/${selectedBoard}/reset`, { method: 'POST' });
      if (!res.ok) throw new Error(await res.text());
      reloadEntries();
      reloadBoards();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <Container>
      <PageHeader
        title="Leaderboards"
        subtitle="Score rankings, submission, and board reset."
      />
      {error && <div className="error-banner">{error}</div>}

      {/* Board selector */}
      {boards.length > 0 && (
        <div className="card mb-5 p-4">
          <h3 className="text-sm font-semibold mb-3">Boards</h3>
          <div className="flex gap-2 flex-wrap">
            {boards.map(b => (
              <button
                key={b.board_id}
                className={`btn btn-sm ${selectedBoard === b.board_id ? 'btn-primary' : ''}`}
                onClick={() => { setSelectedBoard(b.board_id); setOffset(0); }}
              >
                {b.display_name} ({b.total_entries})
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Score table */}
      {entries.length > 0 && (
        <div className="card mb-5">
          <h3 className="text-sm font-semibold p-4 pb-2">Rankings — {selectedBoard}</h3>
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>#</th><th>Player</th><th>Score</th></tr>
              </thead>
              <tbody>
                {entries.map(e => (
                  <tr key={e.player_id}>
                    <td>{e.rank}</td>
                    <td className="font-mono text-xs">{e.player_id}</td>
                    <td>{e.score}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex gap-2 p-4 pt-2">
            <button className="btn btn-sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))}>Prev</button>
            <button className="btn btn-sm" disabled={entries.length < limit} onClick={() => setOffset(offset + limit)}>Next</button>
            <span className="text-xs text-fg-muted ml-auto">Showing {offset + 1}-{offset + entries.length}</span>
          </div>
        </div>
      )}

      {entries.length === 0 && selectedBoard && (
        <div className="empty mb-5">
          <div className="text-fg-2 font-medium mb-1">No scores yet</div>
          <div className="text-xs text-fg-muted">Submit a score below to get started.</div>
        </div>
      )}

      {/* Submit score */}
      <div className="card mb-5 p-4">
        <h3 className="text-sm font-semibold mb-3">Submit Score</h3>
        <div className="flex gap-3 items-end">
          <div>
            <label className="text-xs text-fg-muted block mb-1">Player ID</label>
            <input className="input" placeholder="player_1" value={submitPlayer} onChange={e => setSubmitPlayer(e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-fg-muted block mb-1">Score</label>
            <input className="input w-24" type="number" placeholder="100" value={submitScore} onChange={e => setSubmitScore(e.target.value)} />
          </div>
          <button className="btn btn-primary" disabled={busy || !submitPlayer.trim() || !submitScore} onClick={submitScoreAction}>Submit</button>
        </div>
      </div>

      {/* Reset board */}
      {selectedBoard && (
        <div className="card p-4">
          <h3 className="text-sm font-semibold mb-3">Reset Board</h3>
          <p className="text-xs text-fg-muted mb-3">Clears all scores and distributes rewards for <strong>{selectedBoard}</strong>.</p>
          <button className="btn btn-danger" disabled={busy} onClick={resetBoard}>Reset Board</button>
        </div>
      )}

      {boards.length === 0 && (
        <div className="empty">
          <div className="text-fg-2 font-medium mb-1">No leaderboards published</div>
          <div className="text-xs text-fg-muted">Create a leaderboard in Config Explorer, publish, then manage here.</div>
        </div>
      )}
    </Container>
  );
}
